import React from 'react';
import { useQueryClient } from 'react-query';
import { HiOutlineArrowPathRoundedSquare } from 'react-icons/hi2';

import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { formatCurrency } from 'Core/utils/numberFormat';
import { OutlineButton } from 'Core/components/Button';
import { getApplicationRef, getExchangeState } from 'Modules/financing/utils/exchangeStatus';
import PreviousApplicationModal from 'Modules/financing/components/PreviousApplicationModal';

const Pill = ({ label, value, 'data-testid': dataTestId }) => (
  <span className="inline-flex items-center gap-1.5 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-xs leading-none">
    {label && <span className="font-bold uppercase tracking-wide text-slate-400">{label}</span>}
    <span className="font-semibold tabular-nums text-slate-900" data-testid={dataTestId}>
      {value}
    </span>
  </span>
);

const isPresent = (value) => value !== null && typeof value !== 'undefined' && value !== '';

// Past this many previous applications the banner stops listing them one by one.
const MAX_PILLS = 3;

const pillLabel = (previousApplication) => {
  const ref = getApplicationRef(previousApplication);
  return isPresent(ref) ? `#${ref}` : '';
};

const pillValue = (previousApplication) => {
  const { amount_to_pay: amountToPay, financed } = previousApplication || {};
  const amount = amountToPay ?? financed;
  return isPresent(amount) ? formatCurrency(Number(amount) || 0, { withDecimals: true }) : '—';
};

/**
 * Rendered at the top of the offer-selection screens and the financing exception screens
 * whenever the response carries previous applications. It flags the post-exchange state and
 * keeps the still-active previous applications one click away, regardless of the exchange
 * trigger status — a rejected exchange leaves them funded and active all the same.
 *
 * Screens that already hold the freshest application pass it in; the rest fall back to the
 * cached next-step response (read only — this never triggers a fetch of its own).
 */
export default function PostExchangeBanner({ application }) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const cachedApplication = queryClient.getQueryData(cacheKeys.financingNextStep);
  // Screens reached by a direct route get no application prop (or an empty default).
  const source =
    application && Object.keys(application).length > 0 ? application : cachedApplication;
  const { isPostExchange, requestedAt, previousApplications } = getExchangeState(source);

  if (!isPostExchange) {
    return null;
  }

  const isPlural = previousApplications.length > 1;
  const visible = previousApplications.slice(0, MAX_PILLS);
  const hiddenCount = previousApplications.length - visible.length;

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-solid border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3.5"
      data-testid="post-exchange-banner"
    >
      <span
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 ring-4 ring-white/70"
      >
        <HiOutlineArrowPathRoundedSquare className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="rounded-full bg-green-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
            Exchange request
          </span>
          <span className="text-sm leading-relaxed text-slate-600">
            {isPlural
              ? 'The previous applications stay funded and active until this one is funded.'
              : 'The initial application stays funded and active until this one is funded.'}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {visible.map((previousApplication, index) => (
            <Pill
              key={getApplicationRef(previousApplication) ?? index}
              label={pillLabel(previousApplication)}
              value={pillValue(previousApplication)}
              data-testid={`post-exchange-previous-${index}`}
            />
          ))}
          {hiddenCount > 0 && (
            <Pill value={`+${hiddenCount} more`} data-testid="post-exchange-previous-more" />
          )}
          {requestedAt && (
            <Pill label="Requested" value={requestedAt} data-testid="post-exchange-requested-at" />
          )}
        </div>
      </div>

      <OutlineButton
        type="button"
        size="small"
        className="!border-green-600 !bg-white !font-semibold !text-green-700 hover:!border-green-700 hover:!bg-green-50"
        onClick={() => setIsModalOpen(true)}
        data-testid="btn-view-previous-application"
      >
        {isPlural ? 'View Previous Applications' : 'View Initial Application'}
      </OutlineButton>

      <PreviousApplicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        previousApplications={previousApplications}
        requestedAt={requestedAt}
      />
    </div>
  );
}
